import {
  HttpErrorResponse,
  type HttpHeaders,
  type HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';

import { buildApiUrl, isApiUrl } from '@core/api/api-url';
import { APP_CONFIG } from '@core/app-config';
import {
  FINGERPRINT_HEADER,
  MY_PERMISSIONS_PATH,
  PermissionsSync,
} from '@core/auth/permissions-sync';

/**
 * Watches back-office responses for a sign that the operator's permissions changed: a new
 * `X-Authz-Fingerprint`, or a 403 `PERMISSION_DENIED`. Either refetches the permission
 * list. The header is a change signal only; access is always decided from the list.
 *
 * Registered after `errorInterceptor`, so it still sees the raw response and its headers.
 */
export const authzInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBaseUrl = inject(APP_CONFIG).apiBaseUrl;
  // Only back office carries the header. The permissions call itself is skipped: it
  // reports the new fingerprint in its body, and noting it here would refetch it again.
  if (
    !isApiUrl(buildApiUrl(apiBaseUrl, 'back-office'), req.url) ||
    isApiUrl(buildApiUrl(apiBaseUrl, MY_PERMISSIONS_PATH), req.url)
  ) {
    return next(req);
  }

  const sync = inject(PermissionsSync);
  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          sync.noteFingerprint(readFingerprint(event.headers));
        }
      },
      error: (error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return;
        }
        sync.noteFingerprint(readFingerprint(error.headers));
        if (error.status === 403 && errorCode(error.error) === 'PERMISSION_DENIED') {
          sync.refresh();
        }
      },
    }),
  );
};

function readFingerprint(headers: HttpHeaders): string | null {
  return headers.get(FINGERPRINT_HEADER);
}

function errorCode(body: unknown): unknown {
  return typeof body === 'object' && body !== null
    ? (body as Record<string, unknown>)['code']
    : undefined;
}
